import VehicleAssignments from '@/schemas/public/VehicleAssignments';
import { Customer } from '@/types/types';
import { SvelteDate, SvelteMap } from 'svelte/reactivity';
import { buildRelatedJobsMap, loadJob, loadSiteVisit } from './init.svelte';
import {
    Job,
    JobPre,
    RelatedJobMap,
    SchedularEmployee,
    SchedularEmployeeAssignment,
    SchedularVehicle,
    SchedularVehicleAssignment,
    SchedularVehicles,
    SiteVisitMap,
    VehicleAssignmentMap,
} from './types';

// Abort controller for cancelling stale requests
let currentFetchController: AbortController | null = null;

// TODO: add error handling and csrf token refetch
export const fetchScheduleable: (input: {
    loading: string[];
    start: Date;
    end: Date;
    vehicleAssignmentMap: VehicleAssignmentMap;
    siteVisitMap?: SiteVisitMap;
    csrfToken: string;
    vehicles: SchedularVehicles;
    relatedJobsMap: RelatedJobMap;
}) => void = async ({ start, end, vehicleAssignmentMap, siteVisitMap, csrfToken, loading, vehicles, relatedJobsMap }) => {
    // Cancel any in-flight fetch
    if (currentFetchController) {
        currentFetchController.abort();
    }
    const controller = new AbortController();
    currentFetchController = controller;

    const loadingIndex = loading.push('fetchJobs') - 1;

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json, text-plain, */*',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': csrfToken,
    };
    const dateParams = `start_date_time=${start.toISOString()}&end_date_time=${end.toISOString()}`;

    (async () => {
        // Fetch all endpoints in parallel
        const vehicleAssignmentsPromise = fetch(`/web-api/schedular-vehicles?${dateParams}`, {
            method: 'GET',
            headers,
            credentials: 'same-origin',
            signal: controller.signal,
        }).then((res) => res.json());

        const jobsPromise = fetch(`/web-api/schedular-jobs?${dateParams}`, {
            method: 'GET',
            headers,
            credentials: 'same-origin',
            signal: controller.signal,
        }).then((res) => res.json());

        const siteVisitsPromise = siteVisitMap
            ? fetch(`/web-api/schedular-site-visits?${dateParams}`, {
                  method: 'GET',
                  headers,
                  credentials: 'same-origin',
                  signal: controller.signal,
              }).then((res) => res.json())
            : Promise.resolve(null);

        const [vehicleAssignmentsData, jobsData, siteVisitsData] = await Promise.all([vehicleAssignmentsPromise, jobsPromise, siteVisitsPromise]);

        // Process vehicle assignments
        const { vehicleAssignments }: { vehicleAssignments: JobPre[] } = vehicleAssignmentsData;
        for (const vehicleAssignment of vehicleAssignments) {
            if (vehicleAssignmentMap.has(vehicleAssignment.id)) continue;
            vehicleAssignment.duplicated = false;
            const parsedVehicleAssignment = $state(loadJob(vehicleAssignment, vehicles));
            vehicleAssignmentMap.set(parsedVehicleAssignment.id, parsedVehicleAssignment);
        }

        // Process unscheduled jobs
        const { jobs }: { jobs: JobPre[] } = jobsData;
        for (const job of jobs) {
            if (vehicleAssignmentMap.has(job.id)) continue;
            const parsedJob = $state(loadUnscheduledJobs(job));
            // @ts-expect-error - loadUnscheduledJobs return type doesn't exactly match VehicleAssignment
            vehicleAssignmentMap.set(job.id, parsedJob);
        }

        // Process site visits
        if (siteVisitMap && siteVisitsData) {
            const { siteVisits }: { siteVisits: any[] } = siteVisitsData;
            for (const siteVisit of siteVisits) {
                if (siteVisitMap.has(siteVisit.id)) continue;
                const parsedSiteVisit = $state(loadSiteVisit(siteVisit, vehicles));
                siteVisitMap.set(parsedSiteVisit.id, parsedSiteVisit);
            }
        }

        buildRelatedJobsMap(vehicleAssignmentMap, relatedJobsMap);
    })()
        .catch((e) => {
            if (e.name === 'AbortError') return; // Stale request cancelled, ignore
            throw e;
        })
        .finally(() => {
            loading.splice(loadingIndex);
            if (currentFetchController === controller) {
                currentFetchController = null;
            }
        });
};

export const fetchAvailableEmployees: (input: {
    // loading: string[];
    start: Date;
    end: Date;
    csrfToken: string;
    baseUrl: string;
    employeesMap: SvelteMap<string, SchedularEmployee>;
    vehicleAssignment: SchedularVehicleAssignment;
    vehicleAssignmentMap: SvelteMap<string, SchedularVehicleAssignment>;
    siteVisitMap?: SiteVisitMap;
    overlappingEmployeeAssignments: SvelteMap<
        string,
        { employee_id: string; assignment1: SchedularEmployeeAssignment; assignment2: SchedularEmployeeAssignment }
    >;
}) => void = async ({
    // loading,
    csrfToken,
    baseUrl,
    start,
    end,
    employeesMap,
    vehicleAssignment,
    vehicleAssignmentMap,
    siteVisitMap,
    overlappingEmployeeAssignments,
}) => {
    // const loadingIndex = loading.push("fetchJobs") - 1;
    try {
        //console.log("Fetching available employees for vehicle assignment:", vehicleAssignment);
        // eslint-disable-next-line svelte/prefer-svelte-reactivity
        const url = new URL(`/web-api/schedular-employees?start_date_time=${start.toISOString()}&end_date_time=${end.toISOString()}`, baseUrl);
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json, text-plain, */*',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrfToken,
            },
            credentials: 'same-origin',
        });

        const { employees }: { employees: SchedularEmployee[] } = await res.json();
        const assignmentIndex = buildAssignmentIndex(vehicleAssignmentMap, siteVisitMap);
        for (const emp of employees) {
            const loadedEmployee = $state(loadEmployee(emp));
            employeesMap.set(emp.id, loadedEmployee);
            checkForNewAssignments(employeesMap.get(emp.id)!, vehicleAssignmentMap, siteVisitMap);
            employeesMap.set(
                emp.id,
                getAvailabilityPercentage(vehicleAssignment, vehicleAssignmentMap, siteVisitMap, loadedEmployee, assignmentIndex),
            );
        }

        findOverlappingAssignments(vehicleAssignmentMap, siteVisitMap, overlappingEmployeeAssignments);
    } catch (error) {
        console.error('Error fetching available employees:', error);
    } finally {
        // loading.splice(loadingIndex);
    }
};

function checkForNewAssignments(employee: SchedularEmployee, vehicleAssignmentMap: VehicleAssignmentMap, siteVisitMap?: SiteVisitMap) {
    for (const vehicleAssignment of vehicleAssignmentMap.values()) {
        for (const assignment of vehicleAssignment.employeeAssignments) {
            const exists = employee.employeeAssignments.some((a) => a.id === assignment.id);
            if (assignment.employee_id === employee.id && !exists) {
                employee.employeeAssignments.push(assignment);
            }
        }
    }
    // Also check site visits for employee assignments
    if (siteVisitMap) {
        for (const siteVisit of siteVisitMap.values()) {
            for (const assignment of siteVisit.employeeAssignments) {
                const exists = employee.employeeAssignments.some((a) => a.id === assignment.id);
                if (assignment.employee_id === employee.id && !exists) {
                    employee.employeeAssignments.push(assignment);
                }
            }
        }
    }
}

/**
 * Build an index of employee assignments grouped by employee_id.
 * Iterates all assignments once (O(n)) so lookups are O(1) per employee.
 */
function buildAssignmentIndex(
    vehicleAssignmentMap: VehicleAssignmentMap,
    siteVisitMap: SiteVisitMap | undefined,
): Map<string, SchedularEmployeeAssignment[]> {
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const index = new Map<string, SchedularEmployeeAssignment[]>();

    for (const va of vehicleAssignmentMap.values()) {
        for (const ea of va.employeeAssignments) {
            if (ea.deleted || !ea.start_date_time || !ea.end_date_time) continue;
            let list = index.get(ea.employee_id);
            if (!list) {
                list = [];
                index.set(ea.employee_id, list);
            }
            list.push(ea);
        }
    }

    if (siteVisitMap) {
        for (const sv of siteVisitMap.values()) {
            for (const ea of sv.employeeAssignments) {
                if (ea.deleted || !ea.start_date_time || !ea.end_date_time) continue;
                let list = index.get(ea.employee_id);
                if (!list) {
                    list = [];
                    index.set(ea.employee_id, list);
                }
                list.push(ea);
            }
        }
    }

    return index;
}

function calculateBusySeconds(assignments: SchedularEmployeeAssignment[], rangeStart: Date, rangeEnd: Date): number {
    let busySeconds = 0;
    const rangeStartMs = rangeStart.getTime();
    const rangeEndMs = rangeEnd.getTime();

    for (const ea of assignments) {
        const startMs = new Date(ea.start_date_time!).getTime();
        const endMs = new Date(ea.end_date_time!).getTime();
        if (endMs <= rangeStartMs || startMs >= rangeEndMs) continue;
        const overlapStart = startMs > rangeStartMs ? startMs : rangeStartMs;
        const overlapEnd = endMs < rangeEndMs ? endMs : rangeEndMs;
        busySeconds += Math.floor((overlapEnd - overlapStart) / 1000);
    }

    return busySeconds;
}

function getAvailabilityPercentage(
    vehicleAssignment: SchedularVehicleAssignment,
    vehicleAssignmentMap: VehicleAssignmentMap,
    siteVisitMap: SiteVisitMap | undefined,
    employee: SchedularEmployee,
    assignmentIndex?: Map<string, SchedularEmployeeAssignment[]>,
): SchedularEmployee {
    if (vehicleAssignment.start === null || vehicleAssignment.end === null) return employee;

    const index = assignmentIndex ?? buildAssignmentIndex(vehicleAssignmentMap, siteVisitMap);
    const employeeAssignments = index.get(employee.id) ?? [];
    const totalSeconds = Math.floor((vehicleAssignment.end.getTime() - vehicleAssignment.start.getTime()) / 1000);
    const busySeconds = calculateBusySeconds(employeeAssignments, vehicleAssignment.start, vehicleAssignment.end);

    let availability = ((totalSeconds - busySeconds) / totalSeconds) * 100;
    if (availability < 0) availability = 0;
    if (availability > 100) availability = 100;

    return {
        ...employee,
        free_percent: availability,
    };
}

function loadEmployee(employee: SchedularEmployee): SchedularEmployee {
    return {
        ...employee,
    };
}

/**
 * Recalculates employee availability locally without making an API call.
 * Use this during drag operations to avoid flooding the server with requests.
 *
 * @param previewStart - Optional preview start time (used during drag to avoid mutating the job)
 * @param previewEnd - Optional preview end time (used during drag to avoid mutating the job)
 */
export const recalculateEmployeeAvailability = (
    employeesMap: SvelteMap<string, SchedularEmployee>,
    vehicleAssignment: SchedularVehicleAssignment,
    vehicleAssignmentMap: SvelteMap<string, SchedularVehicleAssignment>,
    siteVisitMap?: SiteVisitMap,
    overlappingEmployeeAssignments?: SvelteMap<
        string,
        { employee_id: string; assignment1: SchedularEmployeeAssignment; assignment2: SchedularEmployeeAssignment }
    >,
    previewStart?: Date | null,
    previewEnd?: Date | null,
) => {
    const effectiveStart = previewStart ?? vehicleAssignment.start;
    const effectiveEnd = previewEnd ?? vehicleAssignment.end;

    if (effectiveStart === null || effectiveEnd === null) return;

    // Build index once, reuse for all employees
    const index = buildAssignmentIndex(vehicleAssignmentMap, siteVisitMap);

    for (const [id, employee] of employeesMap) {
        const employeeAssignments = index.get(employee.id) ?? [];
        const totalSeconds = Math.floor((effectiveEnd.getTime() - effectiveStart.getTime()) / 1000);
        const busySeconds = calculateBusySeconds(employeeAssignments, effectiveStart, effectiveEnd);

        let availability = ((totalSeconds - busySeconds) / totalSeconds) * 100;
        if (availability < 0) availability = 0;
        if (availability > 100) availability = 100;

        employeesMap.set(id, { ...employee, free_percent: availability });
    }
    if (overlappingEmployeeAssignments) {
        findOverlappingAssignments(vehicleAssignmentMap, siteVisitMap, overlappingEmployeeAssignments);
    }
};

function findOverlappingAssignments(
    vehicleAssignmentMap: VehicleAssignmentMap,
    siteVisitMap: SiteVisitMap | undefined,
    overlappingMap: SvelteMap<string, { employee_id: string; assignment1: SchedularEmployeeAssignment; assignment2: SchedularEmployeeAssignment }>,
) {
    overlappingMap.clear();

    // Collect all assignments from both vehicle assignments and site visits
    const allAssignments: SchedularEmployeeAssignment[] = [];

    for (const vehicleAssignment of vehicleAssignmentMap.values()) {
        allAssignments.push(...vehicleAssignment.employeeAssignments);
    }

    if (siteVisitMap) {
        for (const siteVisit of siteVisitMap.values()) {
            allAssignments.push(...siteVisit.employeeAssignments);
        }
    }

    // Group by employee
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const byEmployee = new Map();
    allAssignments.forEach((assignment) => {
        if (!byEmployee.has(assignment.employee_id)) {
            byEmployee.set(assignment.employee_id, []);
        }
        byEmployee.get(assignment.employee_id).push(assignment);
    });

    byEmployee.forEach((assignments, employeeId) => {
        for (let i = 0; i < assignments.length; i++) {
            for (let j = i + 1; j < assignments.length; j++) {
                if (doAssignmentsOverlap(assignments[i], assignments[j])) {
                    // Create a unique key for this overlap pair
                    const overlapKey = `${assignments[i].id}-${assignments[j].id}`;
                    overlappingMap.set(overlapKey, {
                        employee_id: employeeId,
                        assignment1: assignments[i],
                        assignment2: assignments[j],
                    });
                }
            }
        }
    });
}

function doAssignmentsOverlap(assignment1: SchedularEmployeeAssignment, assignment2: SchedularEmployeeAssignment) {
    if (
        assignment1.deleted ||
        assignment2.deleted ||
        !assignment1.start_date_time ||
        !assignment1.end_date_time ||
        !assignment2.start_date_time ||
        !assignment2.end_date_time
    ) {
        return false;
    }
    const start1 = new Date(assignment1.start_date_time);
    const end1 = new Date(assignment1.end_date_time);
    const start2 = new Date(assignment2.start_date_time);
    const end2 = new Date(assignment2.end_date_time);

    return start1 < end2 && start2 < end1;
}

function loadUnscheduledJobs(job: Job): {
    id: never;
    title: string;
    site: null;
    vehicle: null;
    jobStart: any;
    start: null;
    duration: number;
    modified: boolean;
    hue: number;
    notes: string;
    description: string;
    site_name: string;
    related_job_ids: string[];
    duplicated: boolean;
    employeeAssignments: any[];
    jobEnd: any;
    end: null;
    job: VehicleAssignments & {
        id: string;
        site_name: string;
        title: string;
        job_type: string;
        status: string;
        start: SvelteDate;
        end_date_time: SvelteDate;
        start_start_time: SvelteDate;
        notes: string;
        duration: number;
        hue: number;
        original_job_id: string | null;
        is_task: boolean;
        is_employee: boolean;
        duplicated: boolean;
        related_job_ids: string[] | null;
        offset: number;
        vehicle: SchedularVehicle;
        employeeAssignments: SchedularEmployeeAssignment[];
        modified: boolean;
        customers: Customer[];
        job: Job;
        postcode: string | null;
    };
} {
    let startDateTime = null;
    if (job.start_date_time !== null) {
        startDateTime = new SvelteDate(job.start_date_time);
    }
    let endDateTime = null;
    if (job.end_date_time !== null) {
        endDateTime = new SvelteDate(job.end_date_time);
    }

    //sync up any employee assignments that belong to this job added inside this session

    // $inspect(job, "job", "sites", sites);

    //console.log("Loading job", job.id, "with start date", startDateTime, sites);
    const toReturn: {
        id: never;
        title: string;
        site: null;
        vehicle: null;
        jobStart: any;
        start: null;
        duration: number;
        modified: boolean;
        hue: number;
        notes: string;
        description: string;
        site_name: string;
        related_job_ids: string[];
        duplicated: boolean;
        employeeAssignments: any[];
        jobEnd: any;
        end: null;
        job: VehicleAssignments & {
            id: string;
            site_name: string;
            title: string;
            job_type: string;
            status: string;
            start: SvelteDate;
            end_date_time: SvelteDate;
            start_start_time: SvelteDate;
            notes: string;
            duration: number;
            hue: number;
            original_job_id: string | null;
            is_task: boolean;
            is_employee: boolean;
            duplicated: boolean;
            related_job_ids: string[] | null;
            offset: number;
            vehicle: SchedularVehicle;
            employeeAssignments: SchedularEmployeeAssignment[];
            modified: boolean;
            customers: Customer[];
            job: Job;
            postcode: string | null;
        };
    } = $state({
        id: job.id,
        title: job.title,
        site: null,
        vehicle: null,
        jobStart: startDateTime,
        start: null,
        duration: 86400,
        modified: false,
        hue: job.hue,
        notes: job.notes,
        description: job.notes ?? '',
        site_name: job.site_name,
        related_job_ids: job.related_job_ids ?? [],
        duplicated: job.duplicated,
        employeeAssignments: [],
        jobEnd: endDateTime,
        end: null,
        job: job,
    });
    return toReturn;
}

// export const fetchSites: (input: {
//     loading: string[],
//     csrfToken: string,
//     baseUrl: string,
//     start: Date,
//     end: Date,
// }) => void = async ({
//                         loading,
//                         csrfToken,
//                         baseUrl,
//                         start,
//                         end,
//                     }) => {
//     const loadingIndex = loading.push("fetchJobs") - 1;
//     (async () => {
//         const url = new URL(`/web-api/schedular-sites?start_date_time=${start.toISOString()}&end_date_time=${end.toISOString()}`, baseUrl);
//         const res = await fetch(url, {
//             method: "GET",
//             headers: {
//                 "Content-Type": "application/json",
//                 Accept: "application/json, text-plain, */*",
//                 "X-Requested-With": "XMLHttpRequest",
//                 "X-CSRF-TOKEN": csrfToken,
//             },
//             credentials: "same-origin",
//         });
//
//         const {sites}: { sites: Site[] } = await res.json();
//         console.log("Fetched sites:", sites);
//         for(const site of sites)
//         {
//         }
//     })().finally(() => loading.splice(loadingIndex));
// }
